import { useControlled } from "@reactuses/core";
import type { Key } from "react";
import React, { useCallback, useMemo, useState } from "react";
import type { PopoverProps } from "react-aria-components";
import { Dialog, DialogTrigger, Popover } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { useTextFilter } from "./hooks/use-text-filter";
import Input from "./Input";
import { SelectListbox } from "./Select";
import { SELECT_SEARCH_THRESHOLD } from "./Select/utils";
import { type SelectItem, type SlotClassNames } from "./types";

const dropdown = tv({
  slots: {
    popover:
      "border-border bg-secondary flex w-fit max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-md border",
    listbox: "min-w-48",
  },
});

export function ListboxSelectedIcon(
  props: {
    readonly isSelected: boolean;
    readonly disableAnimation?: boolean;
  } & React.SVGProps<SVGSVGElement>,
) {
  const { isSelected, disableAnimation, ...otherProps } = props;

  return (
    <svg
      aria-hidden="true"
      data-selected={isSelected}
      viewBox="0 0 17 18"
      {...otherProps}
    >
      <polyline
        fill="none"
        points="1 9 7 14 15 4"
        stroke="currentColor"
        strokeDasharray={22}
        strokeDashoffset={isSelected ? 44 : 66}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        style={
          !disableAnimation
            ? {
                transition: "stroke-dashoffset 200ms ease",
              }
            : {}
        }
      />
    </svg>
  );
}

export type DropdownProps<T extends string> = {
  items: readonly SelectItem<T>[];
  onAction: (key: NoInfer<T>) => void;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  closeOnAction?: boolean;
  isNonModal?: boolean;
  "aria-label": string;
  withDialog?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  searchControl?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    ariaLabel: string;
  };
  className?: string;
  classNames?: SlotClassNames<ReturnType<typeof dropdown>>;
  searchbar?: boolean;
  searchbarSize?: Exclude<
    React.ComponentProps<typeof Input>["size"],
    undefined
  >;
} & Omit<PopoverProps, "className"> &
  (
    | {
        children: React.ReactNode;
        triggerRef?: React.RefObject<HTMLElement | null>;
      }
    | {
        children?: undefined;
        triggerRef: React.RefObject<HTMLElement | null>;
      }
  );

export default function Dropdown<T extends string>({
  children,
  items,
  onAction,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  triggerRef,
  isNonModal = false,
  "aria-label": ariaLabel,
  closeOnAction = true,
  withDialog = true,
  className,
  classNames,
  header,
  footer,
  searchControl,
  searchbar: enableSearchbar = items.length > SELECT_SEARCH_THRESHOLD,
  searchbarSize,
  ...popoverProps
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useControlled(isOpenProp, false, setIsOpenProp);
  const [search, setSearch] = useState("");
  const matches = useTextFilter();

  const searchbar = useMemo(
    () =>
      enableSearchbar ? (
        <Input
          color="tertiary"
          className="mx-3 mt-3 mb-2 shrink-0"
          size={searchbarSize}
          placeholder={searchControl?.placeholder ?? "Search for values..."}
          aria-label={searchControl?.ariaLabel}
          value={searchControl?.value ?? search}
          onChange={(e) => {
            if (searchControl) searchControl.onChange(e.target.value);
            else setSearch(e.target.value);
          }}
        />
      ) : null,
    [enableSearchbar, search, searchbarSize, searchControl],
  );

  const handleAction = useCallback(
    (key: Key) => {
      if (closeOnAction) {
        setIsOpen(false);
      }
      onAction(key as T);
    },
    [onAction, setIsOpen, closeOnAction],
  );
  // TODO: Fit the content without shifting its width as search results change.

  const content = useMemo(
    () => (
      <SelectListbox
        items={
          searchControl
            ? items
            : items.filter(({ label }) => matches(search, label))
        }
        onAction={handleAction}
        className={dropdown().listbox({ className: classNames?.listbox })}
        aria-label={ariaLabel}
      />
    ),
    [
      search,
      searchControl,
      matches,
      items,
      handleAction,
      ariaLabel,
      classNames?.listbox,
    ],
  );

  const popover = useMemo(
    () => (
      <Popover
        triggerRef={triggerRef}
        className={dropdown().popover({
          className: twMerge(className, classNames?.popover),
        })}
        isNonModal={isNonModal}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom end"
        {...popoverProps}
      >
        {withDialog ? (
          <Dialog className="flex min-h-0 flex-col">
            {header}
            {searchbar}
            {content}
            {footer}
          </Dialog>
        ) : (
          <>
            {searchbar}
            {header}
            {content}
            {footer}
          </>
        )}
      </Popover>
    ),
    [
      triggerRef,
      className,
      classNames?.popover,
      isNonModal,
      isOpen,
      setIsOpen,
      popoverProps,
      withDialog,
      header,
      searchbar,
      content,
      footer,
    ],
  );

  const out = useMemo(
    () =>
      children === undefined ? (
        popover
      ) : (
        <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
          {children}
          {popover}
        </DialogTrigger>
      ),
    [children, isOpen, popover, setIsOpen],
  );

  return out;
}
